import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
    User,
    Mail,
    Phone,
    Building2,
    Home,
    Layers,
    DoorOpen,
    Lightbulb,
    Wind,
    Shield,
    Lock,
    Video,
    Volume2,
    Droplets,
    Bell,
    ChevronRight,
    ChevronLeft,
    Check,
    AlertCircle
} from "lucide-react";

export default function DesignYourHome() {
    const { t, i18n } = useTranslation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const dir = i18n.language === "ar" ? "rtl" : "ltr";

    // API base URL - adjust based on your environment
    //const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";
    
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        // Step 1: Information
        name: "",
        email: "",
        phone: "",

        // Step 2: Project Type
        projectType: "",

        // Step 3: Building Type
        buildingType: "",

        // Step 4: Number of Floors
        numberOfFloors: "",

        // Step 5: Number of Rooms
        numberOfRooms: "",

        // Step 6: Smart Home Requirements
        lighting: "",
        shutters: false,
        curtains: false,
        airConditioning: false,
        airConditioningType: "",
        heating: false,

        // Step 7: Additional Systems
        intercom: false,
        smartLocks: false,
        surveillanceCameras: false,
        soundSystem: false,
        tankLevelSystem: false,
        alarmSystem: false
    });

    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        // Clean the phone number
        let cleanedPhone = phone;
        if (cleanedPhone.startsWith('+')) {
            const plus = '+';
            const digits = cleanedPhone.slice(1).replace(/\D/g, '');
            cleanedPhone = plus + digits;
        } else {
            cleanedPhone = cleanedPhone.replace(/\D/g, '');
        }
        
        // Check if we have at least 7 digits (minimum for a phone number)
        // and at most 15 digits (with country code)
        const digitsOnly = cleanedPhone.replace(/\D/g, '');
        return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    };

    const validateName = (name) => {
        return name.trim().length >= 2;
    };

    const validateNumberField = (value, min = 1) => {
        if (value === "" || value === null || value === undefined) {
            return min === 0; // If min is 0, empty string is valid for optional fields
        }
        const num = parseInt(value, 10);
        return !isNaN(num) && num >= min;
    };

    // Validate specific field
    const validateField = (field, value) => {
        switch (field) {
            case 'name':
                return validateName(value);
            case 'email':
                return validateEmail(value);
            case 'phone':
                return validatePhone(value);
            case 'numberOfFloors':
            case 'numberOfRooms':
                return validateNumberField(value, 0);
            default:
                return true;
        }
    };

    // Update form data with validation
    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Validate the field and update errors
        if (fieldErrors[field]) {
            const isValid = validateField(field, value);
            if (isValid) {
                setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field];
                    return newErrors;
                });
            }
        }
    };

    // Validate current step
    const validateCurrentStep = () => {
        const errors = {};

        switch (currentStep) {
            case 0:
                if (!validateName(formData.name)) {
                    errors.name = dir === "rtl" ? "الاسم يجب أن يكون حقيقيًا (على الأقل حرفين)" : "Name must be valid (at least 2 characters)";
                }
                if (!validateEmail(formData.email)) {
                    errors.email = dir === "rtl" ? "البريد الإلكتروني غير صالح" : "Invalid email address";
                }
                if (!validatePhone(formData.phone)) {
                    errors.phone = dir === "rtl" ? "رقم الهاتف غير صالح (يجب أن يكون بين 7 و 15 رقمًا)" : "Invalid phone number (must be 7 to 15 digits)";
                }
                break;

            case 1:
                if (!formData.projectType) {
                    errors.projectType = dir === "rtl" ? "الرجاء اختيار نوع المشروع" : "Please select a project type";
                }
                break;

            case 2:
                if (!formData.buildingType) {
                    errors.buildingType = dir === "rtl" ? "الرجاء اختيار نوع المبنى" : "Please select a building type";
                }
                break;

            case 3:
                if (!validateNumberField(formData.numberOfFloors, 1)) {
                    errors.numberOfFloors = dir === "rtl" ? "يجب أن يكون عدد الطوابق رقمًا صحيحًا موجبًا" : "Number of floors must be a positive integer";
                }
                break;

            case 4:
                if (!validateNumberField(formData.numberOfRooms, 1)) {
                    errors.numberOfRooms = dir === "rtl" ? "يجب أن يكون عدد الغرف رقمًا صحيحًا موجبًا" : "Number of rooms must be a positive integer";
                }
                break;

            case 5:
                if (formData.airConditioning && !formData.airConditioningType) {
                    errors.airConditioningType = dir === "rtl" ? "الرجاء اختيار نوع التكييف" : "Please select air conditioning type";
                }
                break;

            case 6:
                break;
            default:
                // No validation needed for other steps
                break;
        }

        return errors;
    };

    // Check if step is valid
    const isStepValid = () => {
        const errors = validateCurrentStep();
        return Object.keys(errors).length === 0;
    };

    // Load saved data from localStorage on mount
    useEffect(() => {
        const savedData = localStorage.getItem('designYourHomeData');
        if (savedData) {
            setFormData(JSON.parse(savedData));
        }
    }, []);

    // Save data to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('designYourHomeData', JSON.stringify(formData));
    }, [formData]);

    const totalSteps = 7;

    const nextStep = () => {
        const errors = validateCurrentStep();

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        if (currentStep < totalSteps - 1) {
            setFieldErrors({});
            setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setFieldErrors({});
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSubmit = async () => {
        // Final validation before submit
        const errors = validateCurrentStep();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setSubmitting(true);

        try {
            // Get reCAPTCHA token if available
            let token = null;
            if (executeRecaptcha) {
                token = await executeRecaptcha('design_home_form');
            }

            // Prepare data for backend API
            const requestData = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                projectType: formData.projectType,
                buildingType: formData.buildingType,
                numberOfFloors: parseInt(formData.numberOfFloors, 10),
                numberOfRooms: parseInt(formData.numberOfRooms, 10),
                lighting: formData.lighting ? parseInt(formData.lighting, 10) : 0,
                shutters: formData.shutters,
                curtains: formData.curtains,
                airConditioning: formData.airConditioning,
                airConditioningType: formData.airConditioningType || null,
                heating: formData.heating,
                intercom: formData.intercom,
                smartLocks: formData.smartLocks,
                surveillanceCameras: formData.surveillanceCameras,
                soundSystem: formData.soundSystem,
                tankLevelSystem: formData.tankLevelSystem,
                alarmSystem: formData.alarmSystem
            };

            // Include captcha token if available
            if (token) {
                requestData.captchaToken = token;
            }

            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            // Send to backend API
            const response = await fetch(`${apiUrl}/api/design-home`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(requestData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            //const result = await response.json();
            
            setSubmitStatus('success');
            localStorage.removeItem('designYourHomeData');
            
            // Reset form after 3 seconds
            setTimeout(() => {
                setFormData({
                    name: "", email: "", phone: "", projectType: "", buildingType: "",
                    numberOfFloors: "", numberOfRooms: "", lighting: "", shutters: false,
                    curtains: false, airConditioning: false, airConditioningType: "",
                    heating: false, intercom: false, smartLocks: false, surveillanceCameras: false,
                    soundSystem: false, tankLevelSystem: false, alarmSystem: false
                });
                setCurrentStep(0);
                setSubmitStatus(null);
                setFieldErrors({});
            }, 3000);
            
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
            // You might want to show the error message to the user
            // setSubmitMessage(error.message || "حدث خطأ، حاول مرة أخرى");
        } finally {
            setSubmitting(false);
        }
    };

    // ... [rest of the renderStep function remains the same until the return statement]
    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("information")}
                        </h2>

                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: '#2d2d2b' }}>
                                <User size={18} className="inline mr-2" />
                                {t("contact.name")} *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => updateFormData('name', e.target.value)}
                                onBlur={() => {
                                    if (!validateName(formData.name)) {
                                        setFieldErrors(prev => ({
                                            ...prev,
                                            name: dir === "rtl" ? "الاسم يجب أن يكون حقيقيًا (على الأقل حرفين)" : "Name must be valid (at least 2 characters)"
                                        }));
                                    }
                                }}
                                placeholder={t("contact.namePlaceholder")}
                                className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${fieldErrors.name ? 'border-red-500' : ''}`}
                                style={{ borderColor: fieldErrors.name ? '#ef4444' : 'rgba(255, 222, 33, 0.3)' }}
                            />
                            {fieldErrors.name && (
                                <div className="flex items-center mt-2 text-red-600 text-sm">
                                    <AlertCircle size={16} className="mr-2" />
                                    {fieldErrors.name}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: '#2d2d2b' }}>
                                <Mail size={18} className="inline mr-2" />
                                {t("contact.email")} *
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => updateFormData('email', e.target.value)}
                                onBlur={() => {
                                    if (!validateEmail(formData.email)) {
                                        setFieldErrors(prev => ({
                                            ...prev,
                                            email: dir === "rtl" ? "البريد الإلكتروني غير صالح" : "Invalid email address"
                                        }));
                                    }
                                }}
                                placeholder={t("contact.emailPlaceholder")}
                                className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${fieldErrors.email ? 'border-red-500' : ''}`}
                                style={{ borderColor: fieldErrors.email ? '#ef4444' : 'rgba(255, 222, 33, 0.3)' }}
                            />
                            {fieldErrors.email && (
                                <div className="flex items-center mt-2 text-red-600 text-sm">
                                    <AlertCircle size={16} className="mr-2" />
                                    {fieldErrors.email}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: '#2d2d2b' }}>
                                <Phone size={18} className="inline mr-2" />
                                {t("contact.phone")} *
                            </label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => {
                                    // Allow numbers, plus sign, spaces, and hyphens
                                    const value = e.target.value;
                                    updateFormData('phone', value);
                                }}
                                onBlur={() => {
                                    // Clean the phone number for validation (remove all non-digit except leading +)
                                    let cleanedPhone = formData.phone;
                                    if (cleanedPhone.startsWith('+')) {
                                        const plus = '+';
                                        const digits = cleanedPhone.slice(1).replace(/\D/g, '');
                                        cleanedPhone = plus + digits;
                                    } else {
                                        cleanedPhone = cleanedPhone.replace(/\D/g, '');
                                    }
                                    
                                    // Validate: at least 7 digits, at most 15 digits
                                    const digitsOnly = cleanedPhone.replace(/\D/g, '');
                                    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
                                        setFieldErrors(prev => ({
                                            ...prev,
                                            phone: dir === "rtl" ? "رقم الهاتف غير صالح (يجب أن يكون بين 7 و 15 رقمًا)" : "Invalid phone number (must be 7 to 15 digits)"
                                        }));
                                    } else {
                                        // Clear error if validation passes
                                        setFieldErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.phone;
                                            return newErrors;
                                        });
                                    }
                                }}
                                placeholder="07XXXXXXXX"
                                className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${fieldErrors.phone ? 'border-red-500' : ''}`}
                                style={{ 
                                    borderColor: fieldErrors.phone ? '#ef4444' : 'rgba(255, 222, 33, 0.3)',
                                    direction: 'ltr',
                                    textAlign: 'left'
                                }}
                                dir="ltr"
                            />
                            {fieldErrors.phone && (
                                <div className="flex items-center mt-2 text-red-600 text-sm">
                                    <AlertCircle size={16} className="mr-2" />
                                    {fieldErrors.phone}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("projectType")}
                        </h2>
                        {fieldErrors.projectType && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                <div className="flex items-center text-red-600">
                                    <AlertCircle size={20} className="mr-2" />
                                    {fieldErrors.projectType}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['newBuild', 'renovation'].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => updateFormData('projectType', type)}
                                    className="p-6 rounded-xl border-2 transition-all duration-200 hover:scale-105"
                                    style={{
                                        borderColor: formData.projectType === type ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                        background: formData.projectType === type ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                    }}
                                >
                                    <Building2 size={32} style={{ color: '#ffde21' }} className="mx-auto mb-3" />
                                    <span className="text-lg font-bold" style={{ color: '#2d2d2b' }}>
                                        {t(type)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("building")}
                        </h2>
                        {fieldErrors.buildingType && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                <div className="flex items-center text-red-600">
                                    <AlertCircle size={20} className="mr-2" />
                                    {fieldErrors.buildingType}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['apartment', 'villa', 'residentialBuilding', 'office', 'building'].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => updateFormData('buildingType', type)}
                                    className="p-6 rounded-xl border-2 transition-all duration-200 hover:scale-105"
                                    style={{
                                        borderColor: formData.buildingType === type ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                        background: formData.buildingType === type ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                    }}
                                >
                                    <Home size={32} style={{ color: '#ffde21' }} className="mx-auto mb-3" />
                                    <span className="text-lg font-bold" style={{ color: '#2d2d2b' }}>
                                        {t(type)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("numberOfFloors")}
                        </h2>

                        <div>
                            <Layers size={48} style={{ color: '#ffde21' }} className="mx-auto mb-6" />
                            <input
                                type="number"
                                min="1"
                                value={formData.numberOfFloors}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    updateFormData('numberOfFloors', value);
                                }}
                                onBlur={() => {
                                    if (!validateNumberField(formData.numberOfFloors, 1)) {
                                        setFieldErrors(prev => ({
                                            ...prev,
                                            numberOfFloors: dir === "rtl" ? "يجب أن يكون عدد الطوابق رقمًا صحيحًا موجبًا" : "Number of floors must be a positive integer"
                                        }));
                                    }
                                }}
                                placeholder={dir === "rtl" ? "أدخل عدد الطوابق" : "Enter number of floors"}
                                className={`w-full px-4 py-3 rounded-xl border-2 text-center text-2xl font-bold transition-all duration-200 ${fieldErrors.numberOfFloors ? 'border-red-500' : ''}`}
                                style={{ borderColor: fieldErrors.numberOfFloors ? '#ef4444' : 'rgba(255, 222, 33, 0.3)' }}
                            />
                            {fieldErrors.numberOfFloors && (
                                <div className="flex items-center mt-2 text-red-600 text-sm">
                                    <AlertCircle size={16} className="mr-2" />
                                    {fieldErrors.numberOfFloors}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("numberOfRooms")}
                        </h2>

                        <div>
                            <DoorOpen size={48} style={{ color: '#ffde21' }} className="mx-auto mb-6" />
                            <input
                                type="number"
                                min="1"
                                value={formData.numberOfRooms}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    updateFormData('numberOfRooms', value);
                                }}
                                onBlur={() => {
                                    if (!validateNumberField(formData.numberOfRooms, 1)) {
                                        setFieldErrors(prev => ({
                                            ...prev,
                                            numberOfRooms: dir === "rtl" ? "يجب أن يكون عدد الغرف رقمًا صحيحًا موجبًا" : "Number of rooms must be a positive integer"
                                        }));
                                    }
                                }}
                                placeholder={dir === "rtl" ? "أدخل عدد الغرف" : "Enter number of rooms"}
                                className={`w-full px-4 py-3 rounded-xl border-2 text-center text-2xl font-bold transition-all duration-200 ${fieldErrors.numberOfRooms ? 'border-red-500' : ''}`}
                                style={{ borderColor: fieldErrors.numberOfRooms ? '#ef4444' : 'rgba(255, 222, 33, 0.3)' }}
                            />
                            {fieldErrors.numberOfRooms && (
                                <div className="flex items-center mt-2 text-red-600 text-sm">
                                    <AlertCircle size={16} className="mr-2" />
                                    {fieldErrors.numberOfRooms}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("smartHomeRequirements")}
                        </h2>

                        {fieldErrors.airConditioningType && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                <div className="flex items-center text-red-600">
                                    <AlertCircle size={20} className="mr-2" />
                                    {fieldErrors.airConditioningType}
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Lighting - Number input */}
                            <div className="p-4 rounded-xl border-2" style={{ borderColor: 'rgba(255, 222, 33, 0.3)' }}>
                                <div className="flex items-center mb-3">
                                    <Lightbulb size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                    <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                        {t("lighting")}
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    value={formData.lighting}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        updateFormData('lighting', value);
                                    }}
                                    placeholder={t("lightsPerFloor")}
                                    className="w-full px-4 py-2 rounded-lg border"
                                    style={{ borderColor: 'rgba(255, 222, 33, 0.3)' }}
                                />
                            </div>

                            {[
                                { key: 'shutters', icon: DoorOpen },
                                { key: 'curtains', icon: DoorOpen },
                                { key: 'heating', icon: Wind }
                            ].map(({ key, icon: Icon }) => (
                                <label
                                    key={key}
                                    className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-102"
                                    style={{
                                        borderColor: formData[key] ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                        background: formData[key] ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData[key]}
                                        onChange={(e) => updateFormData(key, e.target.checked)}
                                        className="w-5 h-5 mr-3"
                                    />
                                    <Icon size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                    <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                        {t(key)}
                                    </span>
                                </label>
                            ))}

                            {/* Air Conditioning with sub-options */}
                            <div>
                                <label
                                    className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                    style={{
                                        borderColor: formData.airConditioning ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                        background: formData.airConditioning ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.airConditioning}
                                        onChange={(e) => {
                                            updateFormData('airConditioning', e.target.checked);
                                            if (!e.target.checked) {
                                                updateFormData('airConditioningType', '');
                                                setFieldErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.airConditioningType;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        className="w-5 h-5 mr-3"
                                    />
                                    <Wind size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                    <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                        {t("airConditioning")}
                                    </span>
                                </label>

                                {formData.airConditioning && (
                                    <div className="ml-12 mt-3 space-y-2">
                                        {['central', 'split'].map((type) => (
                                            <label key={type} className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="airConditioningType"
                                                    checked={formData.airConditioningType === type}
                                                    onChange={() => updateFormData('airConditioningType', type)}
                                                    className="w-4 h-4 mr-2"
                                                />
                                                <span style={{ color: '#2d2d2b' }}>{t(type)}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-black mb-6" style={{ color: '#2d2d2b' }}>
                            {t("additionalSystems")}
                        </h2>

                        <div className="space-y-4">
                            {/* Intercom - Checkbox */}
                            <label
                                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                style={{
                                    borderColor: formData.intercom ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                    background: formData.intercom ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.intercom}
                                    onChange={(e) => updateFormData('intercom', e.target.checked)}
                                    className="w-5 h-5 mr-3"
                                />
                                <Bell size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                    {t("intercomSystem")}
                                </span>
                            </label>

                            {/* Smart Locks - Checkbox */}
                            <label
                                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                style={{
                                    borderColor: formData.smartLocks ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                    background: formData.smartLocks ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.smartLocks}
                                    onChange={(e) => updateFormData('smartLocks', e.target.checked)}
                                    className="w-5 h-5 mr-3"
                                />
                                <Lock size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                    {t("smartLocks")}
                                </span>
                            </label>

                            {/* Surveillance Cameras - Checkbox */}
                            <label
                                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                style={{
                                    borderColor: formData.surveillanceCameras ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                    background: formData.surveillanceCameras ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.surveillanceCameras}
                                    onChange={(e) => updateFormData('surveillanceCameras', e.target.checked)}
                                    className="w-5 h-5 mr-3"
                                />
                                <Video size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                    {t("surveillanceCameras")}
                                </span>
                            </label>

                            {/* Sound System - Checkbox */}
                            <label
                                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                style={{
                                    borderColor: formData.soundSystem ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                    background: formData.soundSystem ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.soundSystem}
                                    onChange={(e) => updateFormData('soundSystem', e.target.checked)}
                                    className="w-5 h-5 mr-3"
                                />
                                <Volume2 size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                    {t("soundSystem")}
                                </span>
                            </label>

                            {/* Tank Level System - Checkbox */}
                            <label
                                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                style={{
                                    borderColor: formData.tankLevelSystem ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                    background: formData.tankLevelSystem ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.tankLevelSystem}
                                    onChange={(e) => updateFormData('tankLevelSystem', e.target.checked)}
                                    className="w-5 h-5 mr-3"
                                />
                                <Droplets size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                    {t("tankLevelSystem")}
                                </span>
                            </label>

                            {/* Alarm System - Checkbox */}
                            <label
                                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                                style={{
                                    borderColor: formData.alarmSystem ? '#ffde21' : 'rgba(255, 222, 33, 0.3)',
                                    background: formData.alarmSystem ? 'rgba(255, 222, 33, 0.1)' : 'white'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.alarmSystem}
                                    onChange={(e) => updateFormData('alarmSystem', e.target.checked)}
                                    className="w-5 h-5 mr-3"
                                />
                                <Shield size={24} style={{ color: '#ffde21' }} className="mr-3" />
                                <span className="font-bold" style={{ color: '#2d2d2b' }}>
                                    {t("alarmSystem")}
                                </span>
                            </label>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div dir={dir} className="min-h-screen py-16" style={{ background: '#fafcf0' }}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Progress Bar */}
                <div className="mb-12">
                    <div className="flex justify-between mb-4">
                        {Array.from({ length: totalSteps }).map((_, index) => (
                            <div
                                key={index}
                                className="flex-1 h-2 rounded-full mx-1 transition-all duration-300"
                                style={{
                                    background: index <= currentStep ? '#ffde21' : 'rgba(255, 222, 33, 0.2)'
                                }}
                            />
                        ))}
                    </div>
                    <p className="text-center text-sm font-bold" style={{ color: '#2d2d2b' }}>
                        {dir === "rtl" ? `الخطوة ${currentStep + 1} من ${totalSteps}` : `Step ${currentStep + 1} of ${totalSteps}`}
                    </p>
                </div>

                {/* Form Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: dir === "rtl" ? -50 : 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white p-8 rounded-3xl shadow-xl"
                        style={{ border: '2px solid rgba(255, 222, 33, 0.3)' }}
                    >
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8">
                    <button
                        onClick={prevStep}
                        disabled={currentStep === 0}
                        className="px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all duration-200 disabled:opacity-30"
                        style={{
                            background: 'white',
                            color: '#2d2d2b',
                            border: '2px solid #2d2d2b'
                        }}
                    >
                        {dir === "rtl" ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                        {dir === "rtl" ? "السابق" : "Previous"}
                    </button>

                    {currentStep === totalSteps - 1 ? (
                        <button
                            onClick={handleSubmit}
                            disabled={!isStepValid() || submitting}
                            className="px-8 py-3 rounded-xl font-bold flex items-center gap-2 transition-all duration-200 hover:shadow-xl disabled:opacity-50"
                            style={{
                                background: '#ffde21',
                                color: '#2d2d2b'
                            }}
                        >
                            {submitting ? (dir === "rtl" ? "جارٍ الإرسال..." : "Sending...") : (
                                <>
                                    <Check size={20} />
                                    {dir === "rtl" ? "إرسال" : "Submit"}
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            onClick={nextStep}
                            disabled={!isStepValid()}
                            className="px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all duration-200 hover:shadow-xl disabled:opacity-50"
                            style={{
                                background: '#ffde21',
                                color: '#2d2d2b'
                            }}
                        >
                            {dir === "rtl" ? "التالي" : "Next"}
                            {dir === "rtl" ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                        </button>
                    )}
                </div>

                {/* Contact Note */}
                <div
                    className="mt-6 p-4 rounded-xl text-center text-sm"
                    style={{
                        background: 'rgba(255, 222, 33, 0.1)',
                        border: '2px solid rgba(255, 222, 33, 0.3)',
                        color: '#2d2d2b'
                    }}
                >
                    {t("contactNote")}
                </div>

                {/* Success/Error Message */}
                {submitStatus && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-6 p-4 rounded-xl text-center font-bold"
                        style={{
                            background: submitStatus === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: submitStatus === 'success' ? '#16a34a' : '#dc2626'
                        }}
                    >
                        {submitStatus === 'success'
                            ? (dir === "rtl" ? "تم الإرسال بنجاح!" : "Submitted successfully!") 
                            : (dir === "rtl" ? "حدث خطأ، حاول مرة أخرى" : "Error occurred, try again")}
                    </motion.div>
                )}
            </div>
        </div>
    );
}